import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../core/services/api/api.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ClientIdService } from '../../../core/services/client-id.service';
import { ToastService } from '../../../core/services/toast/toast.service';
import { AmbassadorPopupDto } from '../../../models/dto/ambassador-popup.dto';
import { EnfafIconComponent } from '../../enfaf-icon/enfaf-icon.component';

@Component({
  selector: 'app-modal-ambassador-popup',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, EnfafIconComponent],
  templateUrl: './modal-ambassador-popup.component.html',
  styleUrl: './modal-ambassador-popup.component.scss'
})
export class ModalAmbassadorPopupComponent implements OnInit {
  protected readonly dialogRef = inject(MatDialogRef<ModalAmbassadorPopupComponent>);
  protected readonly data = inject<AmbassadorPopupDto>(MAT_DIALOG_DATA);
  protected readonly api = inject<ApiService>(ApiService);
  protected readonly router = inject(Router);
  protected readonly toast = inject(ToastService);
  protected readonly auth = inject(AuthService);
  protected readonly clientIdService = inject(ClientIdService);

  ngOnInit(): void {
    this.setupBackdropClickHandler();
  }

  /**
   * Clicking outside the modal closes it without reporting anything to the backend.
   *
   * Marking the popup as shown burns it for good — it never appears to that user again — and a
   * backdrop click is usually accidental, so it used to throw away the invitation on a misclick.
   * Dismissing on purpose is what the X is for (onCloseClick), and that one does mark it as shown.
   */
  private setupBackdropClickHandler(): void {
    this.dialogRef.backdropClick().subscribe(() => {
      this.dialogRef.close({ action: 'dismissed' });
    });
  }

  onModalClick(): void {
    const clientId = this.clientIdService.clientId;
    this.api.toggleAmbassadorPopupAccepted(this.data.id, clientId, true).subscribe(async (res) => {
      const refreshRes = await firstValueFrom(this.auth.refreshToken());
      if (refreshRes.identity?.isAmbassador) {
        this.toast.presentSuccessToast('¡Gracias por unirte al programa de embajadores!');
        this.router.navigateByUrl('/student/ambassador/information');
      } else {
        this.toast.presentErrorToast('No se pudo procesar la solicitud. Por favor, inténtalo de nuevo más tarde.');
      }
      this.dialogRef.close();
    });
  }

  async onCloseClick(event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const clientId = this.clientIdService.clientId;
    this.api.toggleAmbassadorPopupAccepted(this.data.id, clientId, false).subscribe(async (res) => {
      await firstValueFrom(this.auth.refreshToken());
      this.dialogRef.close();
    });
  }

  protected hasPopupImage(): boolean {
    return !!this.data.popupImageUrl?.trim();
  }

  protected getPopupImageUrl(): string | null {
    return this.data.popupImageUrl?.trim() || null;
  }

  protected getDescription(): string {
    return this.data.description?.trim() || '';
  }
}
